// engine-exception: the project workspace composes five task views (table/board/timeline/calendar/workload) + budget burn + plan over the composite /projects/:id/gantt read, which the generic detail screen's tabs cannot express
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMeta, useResource } from '../../engine/api/hooks';
import { useWidgets } from '../../engine/api/context';
import { fieldOf } from '../../engine/meta/types';
import { Button } from '../../engine/design/primitives/Button';
import { Badge, ErrorBanner, PageHeader, Progress, Skeleton, Stat, StatusBadge, type Tone } from '../../engine/design/primitives/Misc';
import { Tabs } from '../../engine/design/primitives/Tabs';
import { RichTextView } from '../../engine/fields/RichText';
import { renderField } from '../../engine/fields/registry';
import { ActivityFeed } from '../../engine/views/ActivityFeed';
import { AttachmentsPanel } from '../../engine/views/AttachmentsPanel';
import { DataGrid } from '../../engine/views/DataGrid';
import { useInspector, useInspectorRouteReset } from '../../engine/views/Inspector';
import { ResourceListScreen } from '../../engine/screens/ResourceListScreen';
import { ResourceFormScreen } from '../../engine/screens/ResourceFormScreen';
import { ActionButtons, resolveActions, useActionRunner } from '../../engine/screens/actions';
import { useHotkeys } from '../../engine/layout/shortcuts';
import { formatDate, formatMinutes, formatMoney } from '../../lib/format';
import { TaskInspector } from './TaskInspector';
import { TasksTable } from './TasksTable';
import { TasksBoard } from './TasksBoard';
import { TasksCalendar, TasksTimeline } from './TasksTimeline';
import { WorkloadGrid } from './WorkloadGrid';
import { day, useBurn, usePlan, useProjectDetail, type BurnRow, type PlanRow } from './data';

// Project detail: header (ref · name · status · health · progress) · meta-driven actions
// (complete / archive / duplicate / status update / save as template) · tabs Overview, Tasks
// (Table | Board | Timeline | Calendar | Workload), Budget, Expenses, Plan, Files, Activity.
// Tasks open in the right-hand Inspector (TaskInspector).

const TABS = ['overview', 'tasks', 'budget', 'expenses', 'plan', 'files', 'activity'] as const;
const VIEWS = ['table', 'board', 'timeline', 'calendar', 'workload'] as const;
type View = (typeof VIEWS)[number];

export function ProjectDetailScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const widgets = useWidgets();
  const inspector = useInspector();
  useInspectorRouteReset(id);
  const meta = useMeta('projects');
  const m = meta.data;
  const detail = useProjectDetail(id);
  const rowQ = useResource<Record<string, unknown>>('projects', id, { expand: ['client', 'owner_tech', 'contract', 'contact', 'site'] });
  const runner = useActionRunner('projects', { onDone: (a) => { detail.refresh(); void rowQ.refetch(); if (a.name === 'archive') navigate('/projects'); } });
  const [editOpen, setEditOpen] = useState(false);
  const [newTask, setNewTask] = useState(false);
  const [newPhase, setNewPhase] = useState(false);
  const [activeTask, setActiveTask] = useState<string | null>(null);

  const tab = TABS.includes(sp.get('tab') as (typeof TABS)[number]) ? (sp.get('tab') as (typeof TABS)[number]) : 'overview';
  const view: View = VIEWS.includes(sp.get('view') as View) ? (sp.get('view') as View) : 'table';
  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(sp);
    if (v === null) next.delete(k);
    else next.set(k, v);
    setSp(next, { replace: true });
  };

  const openTask = (taskId: string) => {
    const t = detail.data?.tasks.find((x) => x.id === taskId);
    setActiveTask(taskId);
    inspector.show({ key: `task:${taskId}`, title: t?.ref ?? 'Task', subtitle: t?.title, content: <TaskInspector taskId={taskId} projectId={id} />, onClose: () => setActiveTask(null) });
  };

  useHotkeys([
    { keys: 'n', run: () => setNewTask(true), when: () => tab === 'tasks', label: 'New task', group: 'Project' },
    { keys: 'e', run: () => setEditOpen(true), when: () => !editOpen, label: 'Edit project', group: 'Project' },
    { keys: '[', run: () => setParam('tab', TABS[Math.max(0, TABS.indexOf(tab) - 1)]!), label: 'Previous tab', group: 'Project' },
    { keys: ']', run: () => setParam('tab', TABS[Math.min(TABS.length - 1, TABS.indexOf(tab) + 1)]!), label: 'Next tab', group: 'Project' },
  ]);

  useEffect(() => {
    if (tab !== 'tasks' && activeTask) inspector.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (meta.error) return <ErrorBanner error={meta.error} />;
  if (detail.error) return <ErrorBanner error={detail.error} action={<Button size="sm" variant="ghost" onClick={() => navigate('/projects')}>Back to projects</Button>} />;
  if (!m || !detail.data) {
    return (
      <div className="hf-page">
        <Skeleton height={28} width={280} />
        <div style={{ marginTop: 16 }}>
          <Skeleton lines={6} />
        </div>
      </div>
    );
  }
  const { project, progress } = detail.data;
  const row = (rowQ.data ?? (project as unknown as Record<string, unknown>)) as Record<string, unknown>;
  const statusField = fieldOf(m, 'status');
  const healthField = fieldOf(m, 'health');
  const tones = (f?: typeof statusField) => Object.fromEntries((f?.enum ?? []).filter((o) => o.tone).map((o) => [o.value, o.tone as Tone]));
  const actions = resolveActions(m, undefined, 'item', row).filter((a) => a.name !== 'recompute');
  // Null when the source cannot say (a Halo list row): hide rather than show an invented 0%.
  const pct = project.progress_percent === null || project.progress_percent === undefined ? null : Number(project.progress_percent);
  const isTemplateProject = Boolean(project.template_id);
  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'file-text' as const },
    { key: 'tasks', label: 'Tasks', icon: 'check-square' as const, count: progress ? `${progress.done_tasks}/${progress.total_tasks}` : undefined },
    { key: 'budget', label: 'Budget', icon: 'dollar-sign' as const },
    { key: 'expenses', label: 'Expenses', icon: 'credit-card' as const },
    ...(isTemplateProject ? [{ key: 'plan', label: 'Plan', icon: 'list-tree' as const }] : []),
    { key: 'files', label: 'Files', icon: 'paperclip' as const },
    { key: 'activity', label: 'Activity', icon: 'activity' as const },
  ];

  const refLabel = (key: string) => {
    const r = row[`${key}_ref`] as { label?: string } | null | undefined;
    return r?.label ?? null;
  };

  let panel: ReactNode;
  if (tab === 'overview') {
    panel = (
      <div className="hf-prj-cols">
        <div>
          <div className="hf-stats">
            <Stat label="Progress" value={pct === null ? "—" : `${Math.round(pct)}%`} sub={progress ? `${progress.done_tasks} of ${progress.total_tasks} tasks done` : undefined} accent />
            <Stat label="Logged" value={formatMinutes(progress?.actual_minutes ?? 0)} sub={progress ? `of ${formatMinutes(progress.estimate_minutes)} estimated` : undefined} />
            <Stat label="Overdue" value={progress?.overdue_tasks ?? '0'} sub="open tasks past due" />
            <Stat label="Target" value={project.target_end_date ? formatDate(project.target_end_date) : '—'} sub={project.start_date ? `from ${formatDate(project.start_date)}` : undefined} />
          </div>
          <section className="hf-prj-section">
            <div className="hf-section-title">About</div>
            <RichTextView html={project.description} />
            <dl className="hf-kv" style={{ marginTop: 'var(--s-4)' }}>
              <div><dt>Client</dt><dd>{refLabel('client') ?? <span className="hf-muted">—</span>}</dd></div>
              <div><dt>Site</dt><dd>{refLabel('site') ?? <span className="hf-muted">—</span>}</dd></div>
              <div><dt>Contact</dt><dd>{refLabel('contact') ?? <span className="hf-muted">—</span>}</dd></div>
              <div><dt>Contract</dt><dd>{refLabel('contract') ?? <span className="hf-muted">—</span>}</dd></div>
              <div><dt>Owner</dt><dd>{refLabel('owner_tech') ?? <span className="hf-muted">—</span>}</dd></div>
              <div><dt>Priority</dt><dd>{renderField(fieldOf(m, 'priority')!, project.priority, row, { widgets })}</dd></div>
              <div><dt>Source</dt><dd>{project.source && typeof project.source['type'] === 'string' ? <span className="hf-muted">{String(project.source['type'])}{project.source['ref'] ? ` · ${String(project.source['ref'])}` : ''}</span> : <span className="hf-muted">manual</span>}</dd></div>
            </dl>
          </section>
          <section className="hf-prj-section">
            <div className="hf-section-title">Budget burn</div>
            <BurnMeters projectId={id} currency={project.currency} />
          </section>
        </div>
        <div>
          <section className="hf-prj-section" style={{ marginTop: 0 }}>
            <ResourceListScreen def={{ path: '', resource: 'project-members', kind: 'list', columns: ['tech_id', 'role'], detailPath: null, inspector: true }} embedded lockedFilters={[{ field: 'project_id', op: 'eq', value: id }]} createInitial={{ project_id: id }} title={<span className="hf-section-title">Members</span>} />
          </section>
          <section className="hf-prj-section">
            <ResourceListScreen def={{ path: '', resource: 'project-status-updates', kind: 'list', columns: ['created_at', 'health', 'summary'], detailPath: null, inspector: true, create: false, query: { sort: '-created_at' } }} embedded lockedFilters={[{ field: 'project_id', op: 'eq', value: id }]} title={<span className="hf-section-title">Status updates</span>} />
          </section>
        </div>
      </div>
    );
  } else if (tab === 'tasks') {
    const viewTabs = [
      { key: 'table', label: 'Table', icon: 'table' as const },
      { key: 'board', label: 'Board', icon: 'kanban' as const },
      { key: 'timeline', label: 'Timeline', icon: 'gantt-chart' as const },
      { key: 'calendar', label: 'Calendar', icon: 'calendar' as const },
      { key: 'workload', label: 'Workload', icon: 'users' as const },
    ];
    panel = (
      <>
        <div className="hf-prj-tools">
          <Tabs variant="pills" tabs={viewTabs} value={view} onChange={(k) => setParam('view', k === 'table' ? null : k)} aria-label="Task view" />
          <div className="hf-row">
            <Button size="sm" icon="list-tree" onClick={() => setNewPhase(true)}>
              New phase
            </Button>
            <Button size="sm" variant="primary" icon="plus" onClick={() => setNewTask(true)}>
              New task
            </Button>
          </div>
        </div>
        {view === 'table' && <TasksTable projectId={id} onOpen={openTask} activeId={activeTask} />}
        {view === 'board' && <TasksBoard projectId={id} onOpen={openTask} />}
        {view === 'timeline' && <TasksTimeline projectId={id} onOpen={openTask} />}
        {view === 'calendar' && <TasksCalendar projectId={id} onOpen={openTask} />}
        {view === 'workload' && <WorkloadGrid techIds={detail.data.members.map((mm) => mm.tech_id)} initialFrom={day(project.start_date) ?? undefined} embedded />}
      </>
    );
  } else if (tab === 'budget') {
    panel = (
      <div className="hf-stack" style={{ gap: 'var(--s-6)' }}>
        <BurnMeters projectId={id} currency={project.currency} />
        <ResourceListScreen def={{ path: '', resource: 'project-budgets', kind: 'list', columns: ['budget_type', 'planned_minutes', 'planned_minor', 'rate_minor', 'currency'], detailPath: null, inspector: true }} embedded lockedFilters={[{ field: 'project_id', op: 'eq', value: id }]} createInitial={{ project_id: id, currency: project.currency }} title={<span className="hf-section-title">Budgets</span>} />
      </div>
    );
  } else if (tab === 'expenses') {
    panel = <ResourceListScreen def={{ path: '', resource: 'project-expenses', kind: 'list', columns: ['occurred_on', 'kind', 'description', 'amount_minor', 'billable', 'task_id'], detailPath: null, inspector: true }} embedded lockedFilters={[{ field: 'project_id', op: 'eq', value: id }]} createInitial={{ project_id: id, currency: project.currency }} title={<span className="hf-section-title">Expenses</span>} />;
  } else if (tab === 'plan') {
    panel = <PlanTab projectId={id} />;
  } else if (tab === 'files') {
    panel = <AttachmentsPanel entityType="project" entityId={id} />;
  } else {
    panel = <ActivityFeed entityType="project" entityId={id} />;
  }

  return (
    <div className="hf-page">
      <PageHeader
        compact
        breadcrumb={[{ label: 'Projects', to: '/projects' }, { label: project.ref }]}
        title={project.name}
        badge={<StatusBadge status={project.status} tones={tones(statusField)} />}
        actions={
          <>
            <ActionButtons actions={actions} onRun={runner.run} ctx={{ id, row, refresh: () => { detail.refresh(); void rowQ.refetch(); } }} max={2} extra={[{ label: 'Edit', icon: 'pencil', hint: 'E', onSelect: () => setEditOpen(true) }]} />
            <Button icon="pencil" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </>
        }
      >
        <div className="hf-prj-head">
          {project.health && (
            <Badge tone={(healthField?.enum?.find((o) => o.value === project.health)?.tone as Tone | undefined) ?? 'muted'}>{healthField?.enum?.find((o) => o.value === project.health)?.label ?? project.health}</Badge>
          )}
          {pct !== null && (
            <span className="hf-prj-meter" title={`${pct}% complete`}>
              <Progress value={pct / 100} tone={pct >= 100 ? 'pos' : undefined} title={`${pct}% complete`} />
              <span className="hf-num">{Math.round(pct)}%</span>
            </span>
          )}
          {refLabel('client') && <span>{refLabel('client')}</span>}
          {project.archived_at && <Badge tone="neg" dot={false}>archived</Badge>}
        </div>
      </PageHeader>
      <Tabs tabs={tabs} value={tab} onChange={(k) => setParam('tab', k === 'overview' ? null : k)} aria-label="Project sections" />
      <div className="hf-tabpanel" role="tabpanel">
        {panel}
      </div>
      {runner.dialogs}
      {editOpen && <ResourceFormScreen resource="projects" id={id} onClose={() => setEditOpen(false)} onDone={() => { setEditOpen(false); detail.refresh(); void rowQ.refetch(); }} />}
      {newTask && <ResourceFormScreen resource="project-tasks" initial={{ project_id: id }} form={{ fields: ['title', 'phase_id', 'kind', 'priority', 'assignee_tech_id', 'start_date', 'due_date', 'estimate_minutes', 'description'] }} onClose={() => setNewTask(false)} onDone={() => { setNewTask(false); detail.refresh(); }} />}
      {newPhase && <ResourceFormScreen resource="project-phases" initial={{ project_id: id }} form={{ fields: ['name', 'start_date', 'end_date', 'colour', 'description'] }} mode="dialog" onClose={() => setNewPhase(false)} onDone={() => { setNewPhase(false); detail.refresh(); }} />}
    </div>
  );
}

function burnLabel(b: BurnRow, currency: string): string {
  if (b.planned_minor !== '0' || b.budget_type !== 'labour') return `${formatMoney(b.actual_minor, b.currency || currency)} / ${formatMoney(b.planned_minor, b.currency || currency)}`;
  return `${formatMinutes(b.actual_minutes)} / ${formatMinutes(b.planned_minutes)}`;
}

/** Burn meters per budget row (v_project_budget_burn). */
export function BurnMeters({ projectId, currency }: { projectId: string; currency: string }) {
  const burn = useBurn(projectId);
  if (burn.error) return <ErrorBanner error={burn.error} />;
  if (!burn.data) return <Skeleton lines={3} />;
  if (burn.data.budgets.length === 0) return <p className="hf-muted">No budget set. Add one under Budget to track burn.</p>;
  const TYPE_LABEL: Record<BurnRow['budget_type'], string> = { labour: 'Labour', expense: 'Expenses', product: 'Products', total: 'Total' };
  return (
    <div className="hf-prj-burn" aria-label="Budget burn">
      {burn.data.budgets.map((b) => {
        const pct = Number(b.burn_percent);
        return (
          <div className="row" key={b.budget_id}>
            <span>{TYPE_LABEL[b.budget_type]}</span>
            <Progress value={pct / 100} tone={pct >= 100 ? 'neg' : pct >= 80 ? 'warn' : undefined} title={`${TYPE_LABEL[b.budget_type]} ${pct}% used`} />
            <span className="nums">
              {burnLabel(b, currency)} <span className="hf-muted">({pct}%)</span>
            </span>
          </div>
        );
      })}
      {burn.data.warning_level > 0 && (
        <p className="hf-muted" style={{ margin: 0 }}>
          Budget warning raised at {burn.data.warning_level}%.
        </p>
      )}
    </div>
  );
}

/** Unborn template tasks (creation_rule not yet satisfied). */
function PlanTab({ projectId }: { projectId: string }) {
  const plan = usePlan(projectId);
  const columns = useMemo(
    () => [
      { id: 'key', header: 'Key', width: 140, cell: (r: PlanRow) => <span className="hf-mono">{r.key}</span> },
      { id: 'title', header: 'Task', width: 320, cell: (r: PlanRow) => <span>{r.title}</span> },
      { id: 'kind', header: 'Kind', width: 110, cell: (r: PlanRow) => <Badge tone="muted" dot={false}>{r.kind}</Badge> },
      { id: 'rule', header: 'Created when', width: 170, cell: (r: PlanRow) => <span className="hf-muted">{r.rule.replace(/_/g, ' ')}</span> },
      { id: 'deps', header: 'After', width: 220, cell: (r: PlanRow) => <span className="hf-mono">{r.depends_on.map((d) => d.key).join(', ') || '—'}</span> },
      { id: 'estimate', header: 'Estimate', width: 110, align: 'right' as const, cell: (r: PlanRow) => <span className="hf-num">{formatMinutes(r.estimate_minutes)}</span> },
    ],
    [],
  );
  return (
    <DataGrid<PlanRow>
      aria-label="Planned tasks"
      columns={columns}
      rows={plan.data?.data ?? []}
      keyOf={(r) => r.id}
      loading={plan.isLoading}
      error={plan.error}
      hideToolbar
      empty={<EmptyPlan />}
    />
  );
}

function EmptyPlan() {
  return <p className="hf-muted">Every template task has been created — nothing is waiting on a predecessor.</p>;
}
